import React, { useMemo, useState } from 'react';
import { X, Trophy, Users } from 'lucide-react';
import ShaderBackground from './ShaderBackground';

interface BracketPreviewModalProps {
  title: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundType: 'shader' | 'image' | 'gradient' | 'none';
  shaderPreset: string;
  backgroundImageUrl?: string | null;
  prizeDisplay?: string | null;
  titleSponsor?: string | null;
  logoUrl?: string | null;
  sponsorLogos?: string[] | null;
  sponsorLinks?: string[] | null;
  onClose: () => void;
}

type Match = { p1: string; p2: string; s1: number | null; s2: number | null; winner: number };

const SIZE_OPTIONS = [4, 8, 16, 32, 64] as const;
type BracketSize = typeof SIZE_OPTIONS[number];

const ROUND_LABEL: Record<number, string> = {
  2: 'Finals',
  4: 'Semi-Finals',
  8: 'Quarter-Finals',
  16: 'Round of 16',
  32: 'Round of 32',
  64: 'Round of 64',
};

// Given matches per side in a half-round, return the correct tournament-wide label.
// Total participants at that round = matchesPerSide * 4 (2 sides * 2 players per match).
const getLabel = (matchesPerSide: number) => {
  const participants = matchesPerSide * 4;
  return ROUND_LABEL[participants] || `Round of ${participants}`;
};

const singerName = (i: number) => {
  // 1 -> A, 26 -> Z, 27 -> AA
  let n = i;
  let name = '';
  while (n > 0) {
    const r = (n - 1) % 26;
    name = String.fromCharCode(65 + r) + name;
    n = Math.floor((n - 1) / 26);
  }
  return `Singer ${name}`;
};

// Build left/right sides of a bracket for a given participant count.
// The "progress" point is: LEFT side is fully resolved, RIGHT side is TBD,
// one semi-final result completed, finals have one participant.
function buildBracket(size: BracketSize): { left: Match[][]; right: Match[][]; finals: Match } {
  const rounds = Math.log2(size); // participants -> total rounds (incl. finals)
  const halfRounds = rounds - 1;  // rounds on each side (not including finals)

  const left: Match[][] = [];
  const right: Match[][] = [];

  let nextLeftWinner = '';
  let nextRightWinner = '';

  for (let r = 0; r < halfRounds; r++) {
    const matchesInRound = size / Math.pow(2, r + 2); // matches on this side, this round
    const leftRound: Match[] = [];
    const rightRound: Match[] = [];

    for (let m = 0; m < matchesInRound; m++) {
      if (r === 0) {
        // First round: everyone named, left fully scored, right unscored
        const li1 = m * 2 + 1;
        const li2 = m * 2 + 2;
        const ri1 = (size / 2) + m * 2 + 1;
        const ri2 = (size / 2) + m * 2 + 2;

        const s1 = 70 + ((m * 13) % 25);
        const s2 = 70 + ((m * 7 + 5) % 25);
        const winner = s1 >= s2 ? 0 : 1;
        leftRound.push({
          p1: singerName(li1),
          p2: singerName(li2),
          s1,
          s2,
          winner,
        });
        rightRound.push({
          p1: singerName(ri1),
          p2: singerName(ri2),
          s1: null,
          s2: null,
          winner: -1,
        });
      } else {
        // later rounds: left round 1 scored, rest TBD (except left round maybe partially shown)
        // For visualization we'll advance only the very first left match of round 1 into round 2.
        const isFirstLeftOfRound = m === 0 && r === halfRounds - 1; // last left round (semi)
        if (isFirstLeftOfRound) {
          // Left semi: p1 filled from round 1 winner, p2 filled, show partial score
          const s1 = 85 + (r * 3);
          leftRound.push({
            p1: nextLeftWinner || 'TBD',
            p2: 'TBD',
            s1,
            s2: null,
            winner: -1,
          });
        } else {
          leftRound.push({ p1: 'TBD', p2: 'TBD', s1: null, s2: null, winner: -1 });
        }
        rightRound.push({ p1: 'TBD', p2: 'TBD', s1: null, s2: null, winner: -1 });
      }
    }

    // Track left side winner of top match round 1 for continuity
    if (r === 0) {
      nextLeftWinner = leftRound[0].winner === 0 ? leftRound[0].p1 : leftRound[0].p2;
      nextRightWinner = 'TBD';
    }

    left.push(leftRound);
    right.push(rightRound);
  }

  // Finals: left side has the advancing singer, right is TBD
  const finals: Match = {
    p1: nextLeftWinner || 'TBD',
    p2: nextRightWinner || 'TBD',
    s1: null,
    s2: null,
    winner: -1,
  };

  return { left, right, finals };
}

// ─── Match row — fixed neutral palette, accent only on score badge ──────────
interface MatchRowProps {
  match: Match;
  isFinals?: boolean;
  side?: 'left' | 'right';
  compact?: boolean;
}

const MatchRow: React.FC<MatchRowProps> = ({ match, isFinals, side = 'left', compact }) => {
  const { p1, p2, s1, s2, winner } = match;
  const rowWidth = isFinals ? 220 : compact ? 150 : 200;
  const rowPadding = compact ? '7px 12px' : '11px 18px';
  const rowFontSize = isFinals ? 16 : compact ? 12 : 15;

  const rowBase: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    padding: rowPadding,
    borderRadius: 2,
    fontSize: rowFontSize,
    fontWeight: 800,
    position: 'relative',
    overflow: 'hidden',
    width: rowWidth,
    boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
  };

  const winnerRow: React.CSSProperties = {
    ...rowBase,
    backgroundColor: '#ffffff',
    border: 'none',
    color: '#0f172a',
    boxShadow: '0 4px 18px rgba(0,0,0,0.55)',
  };
  const loserRow: React.CSSProperties = {
    ...rowBase,
    backgroundColor: '#ffffff',
    border: 'none',
    color: 'rgba(15,23,42,0.45)',
  };
  const neutralRow: React.CSSProperties = {
    ...rowBase,
    backgroundColor: '#ffffff',
    border: 'none',
    color: '#0f172a',
  };
  const tbdRow: React.CSSProperties = {
    ...rowBase,
    backgroundColor: 'rgba(15,23,42,0.75)',
    border: '1px dashed rgba(255,255,255,0.25)',
    color: 'rgba(255,255,255,0.45)',
    fontStyle: 'italic',
    boxShadow: 'none',
  };

  const getRowStyle = (name: string, isWin: boolean, isLose: boolean) => {
    if (name === 'TBD') return tbdRow;
    if (isWin) return winnerRow;
    if (isLose) return loserRow;
    return neutralRow;
  };

  const ScoreBox = ({ score, isWin }: { score: number | null; isWin: boolean }) => {
    const hasScore = score !== null;
    const boxWidth = isFinals ? 52 : compact ? 34 : 46;
    return (
      <div style={{
        width: boxWidth,
        alignSelf: 'stretch',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: hasScore && isWin ? '#22c55e' : '#0f172a',
        color: hasScore && isWin ? '#0f172a' : '#ffffff',
        fontWeight: 900,
        fontSize: isFinals ? 17 : compact ? 12 : 15,
        borderRadius: 2,
        flexShrink: 0,
        boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
      }}>
        {hasScore ? score : <span style={{ opacity: 0.45 }}>—</span>}
      </div>
    );
  };

  const p1Win = winner === 0, p1Lose = winner === 1;
  const p2Win = winner === 1, p2Lose = winner === 0;

  const renderLine = (name: string, score: number | null, isWin: boolean, isLose: boolean) => {
    const nameBox = (
      <div style={getRowStyle(name, isWin, isLose)}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
      </div>
    );
    const scoreBox = <ScoreBox score={score} isWin={isWin} />;
    return (
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 0 }}>
        {side === 'right' ? <>{scoreBox}{nameBox}</> : <>{nameBox}{scoreBox}</>}
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 2 : 4 }}>
      {renderLine(p1, s1, p1Win, p1Lose)}
      {renderLine(p2, s2, p2Win, p2Lose)}
    </div>
  );
};

const RoundPill: React.FC<{ label: string; gold?: boolean; compact?: boolean }> = ({ label, gold, compact }) => (
  <div style={{
    textAlign: 'center',
    padding: compact ? '5px 12px' : '7px 22px',
    borderRadius: 2,
    marginBottom: compact ? 6 : 10,
    fontSize: compact ? 10 : 12,
    fontWeight: 900,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    background: gold
      ? 'linear-gradient(180deg, #475569 0%, #1e293b 100%)'
      : '#0f172a',
    border: gold ? '1px solid rgba(251,191,36,0.4)' : 'none',
    color: '#ffffff',
    boxShadow: gold
      ? '0 2px 14px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)'
      : '0 2px 8px rgba(0,0,0,0.5)',
  }}>
    {label}
  </div>
);

const ColumnSpacer: React.FC<{ width?: number }> = ({ width = 36 }) => (
  <div style={{ width, flexShrink: 0 }} />
);

const BracketPreviewModal: React.FC<BracketPreviewModalProps> = ({
  title,
  primaryColor,
  secondaryColor,
  backgroundType,
  shaderPreset,
  backgroundImageUrl,
  prizeDisplay,
  titleSponsor,
  logoUrl,
  sponsorLogos,
  sponsorLinks,
  onClose,
}) => {
  const accent = primaryColor || '#f97316';
  const secondary = secondaryColor || '#1e3a8a';
  const showShader = backgroundType === 'shader' && shaderPreset;
  const showImage = backgroundType === 'image' && backgroundImageUrl;
  const hasSponsorLogos = Array.isArray(sponsorLogos) && sponsorLogos.length > 0;

  const [size, setSize] = useState<BracketSize>(8);
  const { left, right, finals } = useMemo(() => buildBracket(size), [size]);
  const compact = size >= 32;

  // vertical spacing per round: doubles each round so lines align
  const baseGap = compact ? 10 : 24;
  const rowBlockHeight = compact ? 44 : 74; // approx height of a match block

  return (
    <div className="fixed inset-0 z-[60] flex flex-col overflow-hidden" style={{ fontFamily: "'Inter', sans-serif" }}>

      <div className="absolute inset-0 z-0" style={{ backgroundColor: '#0d1117' }}>
        {showShader && (
          <div className="absolute inset-0">
            <ShaderBackground preset={shaderPreset!} primaryColor={accent} secondaryColor={secondary} />
          </div>
        )}
        {showImage && (
          <>
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${backgroundImageUrl})` }} />
            <div className="absolute inset-0 bg-black/60" />
          </>
        )}
        {!showShader && !showImage && (
          <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse at 50% 20%, ${accent}22 0%, #0d1117 65%)` }} />
        )}
      </div>

      <div className="relative z-10 flex flex-col h-full">

        {/* Top-left: size selector */}
        <div className="absolute top-5 left-5 z-30 flex items-center gap-2 bg-black/60 backdrop-blur-md rounded-lg px-3 py-2 border border-white/10">
          <Users size={14} className="text-white/70" />
          <span className="text-xs font-bold uppercase tracking-wider text-white/70">Size</span>
          <div className="flex gap-1">
            {SIZE_OPTIONS.map(opt => (
              <button
                key={opt}
                onClick={() => setSize(opt)}
                className="px-2.5 py-1 rounded text-xs font-bold transition-colors"
                style={{
                  backgroundColor: size === opt ? accent : 'rgba(255,255,255,0.08)',
                  color: size === opt ? '#0f172a' : 'rgba(255,255,255,0.85)',
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>

        <div className="absolute top-5 right-5 z-30 flex items-center gap-3">
          {prizeDisplay && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 22px 10px 18px',
              background: 'linear-gradient(180deg, #475569 0%, #1e293b 100%)',
              color: '#ffffff',
              fontWeight: 900,
              fontSize: 14,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              clipPath: 'polygon(12px 0, 100% 0, 100% 100%, 0 100%)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.15)',
            }}>
              <Trophy size={16} style={{ color: '#fbbf24' }} />
              {prizeDisplay}
            </div>
          )}
          <button onClick={onClose}
            className="p-2 transition-colors"
            style={{ color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(6px)' }}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-shrink-0 relative flex flex-col items-center" style={{ paddingTop: 24, paddingBottom: 8 }}>
          <div
            style={{
              position: 'relative',
              padding: '18px 80px 22px',
              background: `linear-gradient(180deg, ${secondary} 0%, ${secondary}dd 100%)`,
              clipPath: 'polygon(0 0, 100% 0, 100% 60%, calc(100% - 28px) 100%, 28px 100%, 0 60%)',
              boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
              display: 'flex', alignItems: 'center', gap: 18,
            }}
          >
            {logoUrl && <img src={logoUrl} alt="" style={{ height: 44, width: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }} />}
            <h1 style={{
              fontSize: 'clamp(28px, 4.2vw, 52px)',
              fontWeight: 900,
              color: '#ffffff',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              lineHeight: 1,
              textShadow: '0 3px 8px rgba(0,0,0,0.55)',
              margin: 0,
            }}>
              {title || 'Tournament Bracket'}
            </h1>
          </div>
          {titleSponsor && (
            <div style={{
              marginTop: 10,
              padding: '7px 28px',
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#ffffff',
              backgroundColor: 'rgba(15,23,42,0.85)',
              clipPath: 'polygon(0 0, 100% 0, calc(100% - 12px) 100%, 12px 100%)',
              textShadow: '0 1px 4px rgba(0,0,0,0.6)',
            }}>
              Powered by {titleSponsor}
            </div>
          )}
        </div>

        {/* Bracket — scrollable in all directions when content exceeds viewport */}
        <div
          className="flex-1 overflow-auto"
          style={{
            padding: '32px 24px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100%', minWidth: 'min-content', margin: 'auto', width: 'fit-content' }}>

            {/* LEFT ROUNDS (outermost -> innermost) */}
            {left.map((roundMatches, roundIdx) => {
              const gap = baseGap * Math.pow(2, roundIdx) + rowBlockHeight * (Math.pow(2, roundIdx) - 1);
              const topOffset = (Math.pow(2, roundIdx) - 1) * (rowBlockHeight + baseGap) / 2;
              return (
                <React.Fragment key={`l-${roundIdx}`}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap, flexShrink: 0, paddingTop: topOffset }}>
                    {roundMatches.map((m, mi) => (
                      <div key={mi}>
                        {mi === 0 && <RoundPill label={getLabel(roundMatches.length)} compact={compact} />}
                        {mi !== 0 && <div style={{ height: compact ? 22 : 34 }} />}
                        <MatchRow match={m} compact={compact} />
                      </div>
                    ))}
                  </div>
                  <ColumnSpacer width={compact ? 20 : 32} />
                </React.Fragment>
              );
            })}

            {/* FINALS */}
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', transform: `translateY(-${compact ? 80 : 140}px) scale(${compact ? 1.05 : 1.18})`, transformOrigin: 'center top' }}>
              <RoundPill label="Finals" gold compact={compact} />
              <MatchRow match={finals} isFinals />
            </div>

            {/* RIGHT ROUNDS (innermost -> outermost) */}
            {[...right].reverse().map((roundMatches, revIdx) => {
              const roundIdx = right.length - 1 - revIdx;
              const gap = baseGap * Math.pow(2, roundIdx) + rowBlockHeight * (Math.pow(2, roundIdx) - 1);
              const topOffset = (Math.pow(2, roundIdx) - 1) * (rowBlockHeight + baseGap) / 2;
              return (
                <React.Fragment key={`r-${roundIdx}`}>
                  <ColumnSpacer width={compact ? 20 : 32} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap, flexShrink: 0, paddingTop: topOffset }}>
                    {roundMatches.map((m, mi) => (
                      <div key={mi}>
                        {mi === 0 && <RoundPill label={getLabel(roundMatches.length)} compact={compact} />}
                        {mi !== 0 && <div style={{ height: compact ? 22 : 34 }} />}
                        <MatchRow match={m} side="right" compact={compact} />
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Sponsor bar */}
        <div style={{ flexShrink: 0, position: 'relative', width: '100%' }}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 120,
              height: 26,
              backgroundColor: secondary,
              clipPath: 'polygon(0 100%, 220px 0, 100% 0, 100% 100%)',
              boxShadow: '0 -4px 14px rgba(0,0,0,0.35)',
              zIndex: 1,
            }}
          />
          <div
            style={{
              position: 'relative',
              zIndex: 2,
              padding: '28px 48px',
              backgroundColor: 'rgba(10,15,25,0.98)',
              backdropFilter: 'blur(12px)',
              display: 'flex', alignItems: 'center', gap: 48, overflowX: 'auto',
              height: 120,
              boxShadow: '0 -4px 24px rgba(0,0,0,0.5)',
              borderTop: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            <span style={{ fontSize: 13, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.8)', flexShrink: 0 }}>
              Sponsors
            </span>
            {hasSponsorLogos ? (
              sponsorLogos!.map((url, i) => {
                const link = sponsorLinks?.[i]?.trim();
                const img = <img src={url} alt="" style={{ height: 60, width: 'auto', objectFit: 'contain', flexShrink: 0, opacity: 0.95 }} />;
                return link ? (
                  <a key={i} href={link} target="_blank" rel="noopener noreferrer" style={{ flexShrink: 0 }} title={link}>
                    {img}
                  </a>
                ) : (
                  <span key={i} style={{ flexShrink: 0 }}>{img}</span>
                );
              })
            ) : (
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>
                Your sponsor logos will appear here
              </span>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BracketPreviewModal;
